/**
 * insuranceState.ts — one snapshot of a year of cover in, the whole rail out.
 *
 * `advanceState.ts` FOR AN `insurance_contracts` JUNCTION, AND ITS RULES RATHER THAN NEW ONES. The
 * marks, the row classes (gate / render / record), the two counter predicates and the narrator's
 * precedence are the contract dossier's, imported from `setupState.ts` rather than restated — two
 * spellings of "is this row done" is precisely how the previous screen came to draw `Insurance 3/4`
 * over four empty rows.
 *
 * WHAT IS NEW IS THE SUBJECT, AND IT CARRIES ONE THING THE ADVANCE DOES NOT: TWO INSTRUMENTS. A
 * year the driver borrowed for is an Insurance Premium Advance; a year they paid for in full is an
 * Insurance Premium Payment Record, which advances nothing, charges nothing and prints no repayment
 * block [D-B45]. So the rate and the weekly pair go DORMANT on a Record rather than reading `Not
 * set` — there is no rate to set and no recovery to run — and every sentence that names the
 * document names the one this year is actually on.
 *
 * AND THE SECOND THING IS THAT THE LOAN MAY NOT EXIST AT ALL. 54 live junctions carry no
 * `insurance_loans` row: cover was recorded and nothing was financed. A document cannot be generated
 * over a subject that does not exist, so the agreement's own two rows are dormant there — the same
 * exclusion `advanceState` makes for an advance that has not been created yet, one table down.
 *
 * PURE, AND REACT-FREE. `now` is an input, so the same snapshot always produces the same rail —
 * which is what makes the seal timer, the expiry prefix and the narrator testable at all.
 */

import { differenceInSeconds, format, isValid, parseISO } from 'date-fns';

import { INSTRUMENT_TITLES, type InsuranceInstrument } from '@/lib/agreements/instrument';
import { businessDay, formatCurrency } from '@/lib/format';

import { policyPeriodText } from '../data/policyDraft';
import {
    isCountedRow,
    isRowSettled,
    type AreaGroup,
    type DocumentFact,
    type ManifestRow,
    type Narrator,
    type NarratorAction,
    type RowClass,
    type RowMeta,
    type RowState,
    type SetupProgress,
    type SignatureFact,
} from '../setupState';
import { INSURANCE_AREAS, type InsuranceArea } from './insuranceSteps';

// ─── The row vocabulary ──────────────────────────────────────────────────────

/**
 * The fifteen rows of a policy year.
 *
 * IDS ARE STABLE AND PUBLIC, for `ManifestRowId`'s reason: deep links focus them, the narrator names
 * them, the writer attributes refusals to them, and the canvas blocks carry the same strings as DOM
 * ids.
 */
export type InsuranceRowId =
    | 'pol-value'
    | 'pol-period'
    | 'pol-certificate'
    | 'pol-premium'
    | 'pol-discount'
    | 'pol-amount'
    | 'pol-rate'
    | 'pol-deposit'
    | 'pol-weekly'
    | 'pol-first'
    | 'pol-parties'
    | 'pol-hp-reference'
    | 'pol-hp-signature'
    | 'pol-document'
    | 'pol-sealed';

export type InsuranceRow = ManifestRow<InsuranceArea, InsuranceRowId>;
export type InsuranceAreaGroup = AreaGroup<InsuranceArea, InsuranceRowId>;
export type InsuranceNarrator = Narrator<InsuranceArea, InsuranceRowId>;
export type InsuranceNarratorAction = NarratorAction<InsuranceArea, InsuranceRowId>;

interface InsuranceRowDef {
    id: InsuranceRowId;
    area: InsuranceArea;
    label: string;
    cls: RowClass;
    block: string;
    focus?: string;
}

/**
 * THE FOCUS IDS ARE THE SETUP CANVAS'S OWN, and that is not nostalgia. The certificate row is
 * SHARED between the two screens and mints its ids off one default (`f-insCert`), so the dossier
 * naming the same fields differently would have left one surface's deep link landing nowhere. The
 * two the setup canvas has no field for — the driver's own payment, which is a read there — are the
 * only new ones.
 */
const ROW_DEFS: InsuranceRowDef[] = [
    // 1 ── Policy
    { id: 'pol-value', area: 'policy', label: 'Insured value', cls: 'gate', block: 'b-policy', focus: 'f-insValue' },
    { id: 'pol-period', area: 'policy', label: 'Cover period', cls: 'render', block: 'b-policy', focus: 'f-insStart' },
    { id: 'pol-certificate', area: 'policy', label: 'Certificate', cls: 'record', block: 'b-policy', focus: 'f-insCert' },

    // 2 ── Premium
    { id: 'pol-premium', area: 'premium', label: 'Premium payment', cls: 'gate', block: 'b-premium', focus: 'f-premium' },
    { id: 'pol-discount', area: 'premium', label: 'Discount', cls: 'record', block: 'b-premium', focus: 'f-discount' },

    // 3 ── Advance
    { id: 'pol-amount', area: 'advance', label: 'Premium', cls: 'render', block: 'b-advance', focus: 'f-loanAmount' },
    // THE RATE IS A RECORD AND ZERO IS A VALUE, exactly as the advance's is: a premium advanced
    // interest-free is a real arrangement, and a row reading 0 as "not filled in" would ask an admin
    // to correct a deliberate figure.
    { id: 'pol-rate', area: 'advance', label: 'Interest rate', cls: 'record', block: 'b-advance', focus: 'f-loanRate' },
    { id: 'pol-deposit', area: 'advance', label: 'Paid by the driver', cls: 'record', block: 'b-deposit', focus: 'f-deposit' },
    { id: 'pol-weekly', area: 'advance', label: 'Weekly recovery', cls: 'render', block: 'b-recovery', focus: 'f-loanInstalment' },
    { id: 'pol-first', area: 'advance', label: 'Recovery starts', cls: 'render', block: 'b-recovery', focus: 'f-loanInstalmentStart' },

    // 4 ── Agreement
    { id: 'pol-parties', area: 'agreement', label: 'Parties', cls: 'render', block: 'b-parties' },
    { id: 'pol-hp-reference', area: 'agreement', label: 'Hire purchase reference', cls: 'render', block: 'b-insloanagr' },
    { id: 'pol-hp-signature', area: 'agreement', label: 'Hire purchase signature', cls: 'gate', block: 'b-insloanagr' },
    { id: 'pol-document', area: 'agreement', label: 'The instrument', cls: 'render', block: 'b-insloanagr' },
    { id: 'pol-sealed', area: 'agreement', label: 'Executed', cls: 'gate', block: 'b-insloanagr' },
];

/**
 * The receipt a satisfied group collapses to.
 *
 * THE ADVANCE AREA HAS TWO, because the area describes two different arrangements [D-B45]: a
 * premium Primelane advanced is `Advanced`, and one the driver paid in full is `Paid in full` —
 * which is the whole claim the Payment Record makes.
 */
const AREA_RECEIPTS: Record<InsuranceArea, string> = {
    policy: 'On cover',
    premium: 'Recorded',
    advance: 'Advanced',
    agreement: 'Executed',
};

// ─── The snapshot every row is derived from ──────────────────────────────────

/** The `insurances` row itself — null while the policy is still a draft. */
export interface InsurancePolicyFacts {
    id: number;
    /** `insurances.valued_at` — what the vehicle is insured for. */
    valuedAt: number | null;
    /** `insurances.starts_at` — the day cover incepts. */
    startsAt: string | null;
    /** `insurances.expires_at` — the day it lapses. */
    expiresAt: string | null;
    /** `insurances.certificate_url` — the filed Certificate of Insurance, or null. */
    certificateUrl: string | null;
    /** `insurances.certificate_attested_at` — set only where the document could not be read [D-B56]. */
    certificateAttestedAt: string | null;
}

/** The payment made to the insurer, and the credit that came back. Both are transaction links. */
export interface InsuranceMoneyFact {
    id: number;
    transactionId: number | null;
    amount: number | null;
    /** `transactions.transaction_at` — an instant, read on the Ghana business day. */
    paidOn: string | null;
}

/** The `insurance_loans` row — null on the 54 junctions that financed nothing. */
export interface InsuranceLoanFacts {
    id: number;
    /** `insurance_loans.amount` — the PREMIUM the year was written over, never the amount advanced. */
    amount: number | null;
    /** `insurance_loans.interest_rate`, per cent. Nullable in SQL, so null is a real absence. */
    interestRate: number | null;
}

/**
 * One `insurance_loan_instalments` row.
 *
 * `starts_at` IS A DATE COLUMN, so PostgREST hands over `'YYYY-MM-DD'` and a STRING comparison is
 * the chronological one — no parse, no zone, and no host clock moving a boundary.
 */
export interface InsuranceInstalmentFact {
    amount: number | null;
    startsAt: string | null;
}

/** The borrower, as both instruments print them. */
export interface InsuranceDriverFacts {
    fullName: string | null;
    phone: string | null;
    address: string | null;
}

/**
 * The vehicle, as the insurance instruments print it — WHICH IS TWO FIELDS MORE THAN THE ADVANCE'S.
 *
 * `insurance_loan` v7 and `insurance_payment` v1 both carry `Year of manufacture` and `Colour` in
 * their particulars grid; the operational advance's Schedule carries neither. Seven party facts
 * rather than five is that difference, and it is why the Parties row here counts to seven.
 */
export interface InsuranceVehicleFacts {
    make: string | null;
    model: string | null;
    registration: string | null;
    /** `vehicles.year_of_manufacture` — `{{vehicle.year_of_manufacture}}`. */
    year: number | string | null;
    /** `vehicles.colour` — `{{vehicle.colour}}`. */
    colour: string | null;
}

/**
 * The INSURANCE_LOAN slot as the board derived it, carried rather than re-derived.
 *
 * `buildBoardSlots` mints this row from the same `insurance_loan_agreements` and
 * `agreement_documents` rows `agreement_sign` reads; copying it here is what keeps the rail and the
 * board — which is drawn INSIDE the agreement area — from answering the same question two ways.
 */
export interface InsuranceSlotFact {
    /** `insurance_loan_agreements.signed_at` — the paper original's date, or the seal's. */
    legacySignedAt: string | null;
    /** `insurance_loan_agreements.agreement_url` — the filed PDF. Both halves are the seal. */
    legacyUrl: string | null;
    /** `insurance_loan_agreements.origin` — NULL or `'SCANNED'` is a paper original. */
    legacyOrigin: string | null;
    /**
     * WHICH OF THE TWO INSTRUMENTS THIS SLOT IS, as the board derived it from the loan's own rows.
     *
     * IT IS NOT THE SAME QUESTION AS `snapshot.instrument`, AND THE DIFFERENCE IS THE POINT. This
     * one is about the document that exists; the snapshot's is about the DRAFT on screen. They
     * disagree for exactly as long as it takes an admin to link a payment that covers the premium
     * and save it, and the countersign gate below has to read the document's own shape.
     */
    instrument?: InsuranceInstrument | null;
    /**
     * [D-B53] — this contract's hire purchase agreement came back on paper.
     *
     * OPTIONAL, AND ABSENT MEANS FALSE, which keeps the witness arm and goes on withholding the
     * countersignature: the direction that never offers a button into a server refusal.
     */
    masterOnPaper?: boolean;
    document: DocumentFact | null;
    /** The board's own lock sentence (`advanceLockedFor`), or null. */
    blockedReason: string | null;
}

/**
 * WHAT THE CONTRACT'S HIRE PURCHASE AGREEMENT IS, as far as this page can tell [D-B57].
 *
 * `'PAPER'` is the server's own conjunction: a signed `contract_agreements` DRIVER row of
 * `COALESCE(origin,'SCANNED') = 'SCANNED'` AND no live hire purchase document. `'NOT_PAPER'` is
 * every other answered state — signed in the app, or not signed at all. `'UNREAD'` is the board
 * coming back refused, which is NOT the same as `'NOT_PAPER'` and must never be flattened into it.
 */
export type HpClass = 'PAPER' | 'NOT_PAPER' | 'UNREAD';

export interface InsuranceSnapshot {
    contractId: number;
    /** The clock. Every elapsed figure and the expiry prefix are measured from here. */
    now?: Date | string;
    /** The create flow: nothing is saved yet, and every row reads off `drafts`. */
    isNew: boolean;
    /** The saved `insurances` row. Null on a new policy. */
    policy: InsurancePolicyFacts | null;
    premium: InsuranceMoneyFact | null;
    /** The credit the insurer returned. It hangs off the premium, never off the policy. */
    discount: InsuranceMoneyFact | null;
    loan: InsuranceLoanFacts | null;
    /**
     * `insurance_loan_deposits`, with the money off the TRANSACTIONS behind them [D-B45] — the link
     * row carries no amount of its own. Every one of them, not only the one the dossier binds.
     */
    deposits: InsuranceMoneyFact[];
    /** `insurance_loan_instalments`, as read. Empty where nothing is being recovered. */
    instalments: InsuranceInstalmentFact[];
    /**
     * Which instrument the DRAFT would be published on — `insuranceInstrument(draft)`, decided by
     * the page and carried here.
     *
     * THE DRAFT'S AND NOT THE SAVED ROWS', because this rail describes the screen an admin is
     * looking at: a payment linked but not yet saved has already made the weekly recovery
     * contradictory, and a rail still asking for one would be asking for a figure the save is about
     * to drop [D-B45].
     */
    instrument: InsuranceInstrument;
    /**
     * WILL THIS SAVE WRITE AN `insurance_loan_instalments` ROW? — `willWriteInstalment(form)`,
     * decided by the page and carried here.
     *
     * THE FORM'S ANSWER AND NOT THE SAVED ROWS', for `instrument`'s own reason [D-B45]: a weekly
     * recovery half-typed is already an arrangement the year is about to be on, and a rail that
     * said "settled at the end of the contract" over a figure somebody is in the middle of entering
     * would be describing the year the admin has just stopped writing [D-B57].
     */
    writesInstalment: boolean;
    /**
     * WILL THIS SAVE LEAVE THE JUNCTION CARRYING AN `insurance_loans` ROW? — the premium box's own
     * answer, decided by the page and carried here [D-B57].
     *
     * `advanceStatements` WRITES NOTHING AT ALL where the draft carries no premium and the junction
     * no loan, and 54 live junctions are that shape: cover recorded, nothing financed. This is that
     * predicate's first limb; the second is `loan` above, and the two are read together wherever a
     * question is only worth asking of a year that finances something.
     */
    writesLoan: boolean;
    /**
     * WHAT THE CONTRACT'S HIRE PURCHASE AGREEMENT IS [D-B57], off the board's own slots.
     *
     * IT IS NOT `isPaperInsurance` AND THE TWO ARE ABOUT DIFFERENT DOCUMENTS. That one asks whether
     * THIS YEAR'S OWN instrument came back as a scan, and answers the Agreement area; this one asks
     * what the CONTRACT's hire purchase agreement is, which is what decides whether a premium
     * advance may be settled at the end of the contract at all.
     */
    hpClass: HpClass;
    /** How many junctions the contract has in all — 1 is ordinary, >1 is a renewal chain [D-B17]. */
    chainLength: number;
    driver: InsuranceDriverFacts;
    vehicle: InsuranceVehicleFacts;
    /**
     * `contract.hire_purchase_reference` as the server would resolve it — the live HP document's
     * designation, else the paper one `P-<id>` [D-B52]. Null is an instrument that would print the
     * merge key literally.
     */
    hpReference: string | null;
    /** The driver has signed the hire purchase agreement — a paper row, or a signed in-app seat. */
    hpDriverSigned: boolean;
    /** The witness has signed the witness agreement — the other arm of the countersign gate. */
    witnessSigned: boolean;
    slot: InsuranceSlotFact | null;
    /** What `agreement_render` says the wording is still missing, once a document exists. */
    renderMissing: string[] | null;
    /**
     * The instrument is frozen — `agreement_documents.frozen_at`, set at the FIRST signature.
     *
     * The figures the document printed cannot move underneath it: the advance canvas's figures and
     * the cover period render as reads, and the correction is void-and-reissue on the agreement row.
     */
    frozen: boolean;
    /** Unsaved local edits, by row. Shown as a modifier; never counted as progress. */
    drafts?: Partial<Record<InsuranceRowId, string>>;
}

export interface InsuranceState {
    rows: InsuranceRow[];
    areaGroups: InsuranceAreaGroup[];
    progress: SetupProgress;
    narrator: InsuranceNarrator;
}

// ─── Small helpers, the twins of `advanceState`'s ────────────────────────────

function asDate(value: string | Date | null | undefined): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : parseISO(value);
    return isValid(date) ? date : null;
}

/** The app's one date shape, read on the Ghana business day. */
function dateText(value: string | Date | null | undefined): string {
    const date = businessDay(value);
    return date ? format(date, 'MMM d, yyyy') : '';
}

function money(amount: number | null | undefined): string {
    return amount === null || amount === undefined ? '' : formatCurrency(amount);
}

function isFilled(value: string | null | undefined): boolean {
    return !!value && value.trim().length > 0;
}

/** The server gate on an agreement: a signature date AND a stored PDF. Both halves, always. */
export function isInsuranceSealed(slot: InsuranceSlotFact | null): boolean {
    return !!slot && !!slot.legacySignedAt && isFilled(slot.legacyUrl);
}

/**
 * A PAPER INSTRUMENT — signed off the app and permanently so.
 *
 * `agreement_create` refuses a subject whose legacy row is signed with
 * `COALESCE(origin,'SCANNED') = 'SCANNED'`, so this is not a state the flow can leave: the
 * Agreement area reads the record and offers no generation.
 *
 * THIS IS THE INSURANCE INSTRUMENT'S OWN SCAN AND NOT `snapshot.hpClass` [D-B57], which is the
 * same question asked of the CONTRACT's hire purchase agreement. A year may be papered on either,
 * both or neither, and the two answers are read off different rows.
 */
export function isPaperInsurance(slot: InsuranceSlotFact | null): boolean {
    if (!slot || !slot.legacySignedAt) return false;
    return (slot.legacyOrigin ?? 'SCANNED').toUpperCase() === 'SCANNED';
}

const SIGNED_STATUSES = new Set(['SIGNED']);

function signedCount(document: DocumentFact | null): number {
    return (document?.signatures ?? []).filter((row) => SIGNED_STATUSES.has(row.status)).length;
}

/** The next party in signing order who has not signed. Signature order is the template's. */
function pendingSignature(document: DocumentFact | null): SignatureFact | null {
    return (
        (document?.signatures ?? []).find(
            (row) => !SIGNED_STATUSES.has(row.status) && row.status !== 'VOIDED',
        ) ?? null
    );
}

function newestSignedAt(document: DocumentFact | null): string | null {
    const dates = (document?.signatures ?? [])
        .map((row) => row.signedAt)
        .filter((value): value is string => !!value)
        .sort();
    return dates.length ? dates[dates.length - 1] : null;
}

/**
 * The WEEKLY figure: the LATEST rate row, by `starts_at`.
 *
 * `>=` AND NOT `>`, which is the server's tie-break: the row recorded last on a shared start date
 * is the current rate on the day it changed.
 */
export function latestInstalment(rows: InsuranceInstalmentFact[]): InsuranceInstalmentFact | null {
    let latest: InsuranceInstalmentFact | null = null;
    for (const row of rows) {
        if (!row.startsAt) continue;
        if (latest === null || row.startsAt >= (latest.startsAt as string)) latest = row;
    }
    return latest;
}

/** The FIRST recovery: `MIN(starts_at)`, the only date this schema records for when it begins. */
export function firstStartsAt(rows: InsuranceInstalmentFact[]): string | null {
    let earliest: string | null = null;
    for (const row of rows) {
        if (!row.startsAt) continue;
        if (earliest === null || row.startsAt < earliest) earliest = row.startsAt;
    }
    return earliest;
}

/** Σ of the transactions behind the deposit links — what the driver has paid towards the premium. */
export function depositTotal(rows: InsuranceMoneyFact[]): number | null {
    const amounts = rows.map((row) => row.amount).filter((amount): amount is number => amount !== null && amount !== undefined);
    if (amounts.length === 0) return null;
    return amounts.reduce((sum, amount) => sum + amount, 0);
}

/** The latest day any of them was paid — MAX, which is order-free over the list. */
function latestPaidOn(rows: InsuranceMoneyFact[]): string | null {
    let latest: string | null = null;
    for (const row of rows) {
        if (!row.paidOn) continue;
        if (latest === null || row.paidOn > latest) latest = row.paidOn;
    }
    return latest;
}

/**
 * NOTHING IS PAID TOWARDS THIS PREMIUM — one reading of the deposits, asked in one place.
 *
 * `depositRow` DRAWS THE FACE FROM IT AND `rowDormantOf` DRAWS THE MARK, so the two cannot come
 * apart: a row reading `Nothing paid` under a dot that says otherwise would be the rail arguing
 * with itself. `depositTotal`'s own `null` is the question — no link, or none carrying an amount.
 */
function nothingPaid(snapshot: InsuranceSnapshot): boolean {
    return depositTotal(snapshot.deposits) === null;
}

function driverName(snapshot: InsuranceSnapshot): string {
    return isFilled(snapshot.driver.fullName) ? (snapshot.driver.fullName as string) : 'the driver';
}

/** Nothing is saved yet: the create flow, and the one state where the ceremony has no subject. */
function noPolicyYet(snapshot: InsuranceSnapshot): boolean {
    return snapshot.isNew || !snapshot.policy;
}

/**
 * NOTHING WAS FINANCED, so there is no subject for an instrument.
 *
 * `insurance_loans` is what the INSURANCE_LOAN document hangs off, and 54 live junctions carry no
 * loan at all: cover recorded, nothing advanced, nothing to paper. A document row that read `Not
 * generated` on those would be asking an admin to generate a document over a subject that does not
 * exist — which `agreement_create` refuses, and rightly.
 */
function noLoanYet(snapshot: InsuranceSnapshot): boolean {
    return noPolicyYet(snapshot) || !snapshot.loan;
}

/** The title of the instrument this year's figures would be published on [D-B45]. */
function instrumentTitle(snapshot: InsuranceSnapshot): string {
    return INSTRUMENT_TITLES[snapshot.instrument];
}

/** A Payment Record prints no repayment block at all — nothing is advanced and nothing is repaid. */
function isPaymentRecord(snapshot: InsuranceSnapshot): boolean {
    return snapshot.instrument === 'PAYMENT_RECORD';
}

/**
 * IS ANYTHING FINANCED ON THIS YEAR? — `advanceStatements`' own opening question [D-B57].
 *
 * A LOAN ON FILE, OR A PREMIUM IN THE BOX THAT WILL WRITE ONE. The writer returns an empty list of
 * statements where neither holds, so on those 54 junctions nothing about an advance is true and
 * nothing about one should be said. It is the draft's answer as well as the junction's because the
 * canvas proof-reads the page the save is about to publish: a premium typed on a loan-less junction
 * WILL write a loan, and the arrangement it comes back on is a fact about that page.
 */
export function financesAdvance(snapshot: InsuranceSnapshot): boolean {
    return snapshot.writesLoan || !noLoanYet(snapshot);
}

/**
 * WOULD THIS SAVE PUBLISH AN ADVANCE SETTLED AT THE END OF THE CONTRACT? [D-B57]
 *
 * THE SHAPE THE DOCUMENT WOULD BE PUBLISHED ON, asked of the FORM throughout: a paper hire
 * purchase agreement, something actually financed, a premium the driver has not covered, and a save
 * that writes no weekly recovery. Nothing separate is added to the weekly while the vehicle is
 * being paid for; the instalments the driver goes on paying after that recover the advance.
 *
 * THE FINANCING LIMB IS WHY A JUNCTION THAT FINANCED NOTHING IS LEFT ALONE. `instrumentFor` falls
 * back to the Advance on a null premium, so without it a year that only ever recorded cover would
 * read as an advance settled at the end of a contract it never borrowed against.
 *
 * `'UNREAD'` AND `'NOT_PAPER'` BOTH ANSWER NO, which is the conservative direction and the only
 * safe one: a board that could not be read has established nothing about the contract, and saying
 * "settled at the end" off an absence would be stating an arrangement on the strength of a failed
 * request.
 */
export function writesAtEndAdvance(snapshot: InsuranceSnapshot): boolean {
    if (snapshot.hpClass !== 'PAPER') return false;
    if (!financesAdvance(snapshot)) return false;
    return !isPaymentRecord(snapshot) && !snapshot.writesInstalment;
}

/**
 * IS THIS YEAR settled at the end of the contract — the rail's own question [D-B57].
 *
 * IT IS `writesAtEndAdvance` AND ONE MORE THING: no `insurance_loan_instalments` row on file. The
 * rail describes the YEAR, and a year carrying a live weekly recovery is being recovered weekly
 * whatever the boxes say — clearing them is a delete the canvas announces, and the rows go on
 * reading the recovery until it is actually gone.
 */
export function isAtEndAdvance(snapshot: InsuranceSnapshot): boolean {
    return writesAtEndAdvance(snapshot) && snapshot.instalments.length === 0;
}

/** The day cover ended, where it is in the past — the prefix the narrator leads the live states with. */
export function coverEndedOn(snapshot: InsuranceSnapshot, now: Date): string | null {
    const expires = businessDay(snapshot.policy?.expiresAt);
    if (!expires) return null;
    const today = businessDay(now);
    if (!today || expires.getTime() >= today.getTime()) return null;
    return format(expires, 'MMM d, yyyy');
}

// ─── The sentences the rows speak ────────────────────────────────────────────

const CANNOT_CREATE = 'The policy cannot be created without this.';
const PAID_IN_FULL_NO_RATE =
    'The driver paid the whole premium, so nothing is advanced and no interest arises.';
const PAID_IN_FULL_NO_RECOVERY =
    'The driver paid the whole premium, so there is nothing to recover weekly.';
/**
 * [D-B57] — the owner's own words, followed by what they mean for the money.
 *
 * IT NEVER MENTIONS PAPER. The class of the hire purchase agreement is what decides whether this
 * arrangement is open, and an admin reading the row is being told how the advance comes back.
 *
 * AND IT STATES THE RECOVERY RATHER THAN IMPLYING A DUE DATE. Nothing separate is added to the
 * weekly while the vehicle is being paid for; after that the driver keeps paying the contract
 * instalment and those payments recoup the advance. A sentence that stopped at "settled at the end"
 * would leave an admin guessing at how the money actually comes back.
 *
 * THE INVITATION IS THE ONE PART A FROZEN YEAR DOES NOT GET. Once the instrument is signed the
 * canvas withholds the whole advance bar and renders the two boxes as reads, so an offer to set a
 * recovery there would be a sentence the page contradicts two columns over.
 */
const SETTLED_AT_END =
    'No separate weekly is added. Once the vehicle is paid for, the driver keeps paying the ' +
    'contract instalment until the advance is recovered.';
const SETTLED_AT_END_OPEN = `${SETTLED_AT_END} A weekly recovery can still be set here.`;
const HP_SIGNATURE_FIRST = 'The driver has to sign the hire purchase agreement first.';
const DRIVER_BEFORE_COUNTERSIGN =
    'The driver has to sign the hire purchase agreement before Primelane can countersign this.';
const WITNESS_BEFORE_COUNTERSIGN =
    'The witness has to sign the witness agreement before Primelane can countersign this.';
const FROZEN_UNLOCK =
    'The instrument is signed. Void and reissue it from the agreement row to change this.';
const FILING = 'Executed · the signed PDF is still being filed.';
const PREMIUM_QUEUE =
    'Recording the payment is what takes it out of the Unassigned queue.';
const NOTHING_TO_FINANCE =
    'Nothing is financed on this year, so there is no instrument to generate.';

// ─── Per-row derivation ──────────────────────────────────────────────────────

/** The shape every row derivation returns, before the shared plumbing is added. */
interface RowFacts {
    state: RowState;
    detail: string;
    note?: string | null;
    meta?: RowMeta;
}

function done(detail: string, doneDate?: string | null): RowFacts {
    return { state: 'done', detail, meta: doneDate ? { doneDate } : {} };
}

function yourMove(detail: string, note?: string): RowFacts {
    return { state: 'your-move', detail, note: note ?? null };
}

/** A record row: settled either way, with `recorded` carrying which. */
function record(recorded: boolean, detail: string, note?: string): RowFacts {
    return { state: 'done', detail, note: note ?? null, meta: { recorded } };
}

function locked(detail: string, unlockText: string): RowFacts {
    return { state: 'locked', detail, note: unlockText, meta: { unlockText } };
}

/** Whichever sentence names what is missing: the create's, or the document's. */
function blockingNote(snapshot: InsuranceSnapshot): string {
    if (snapshot.isNew) return CANNOT_CREATE;
    return `The ${instrumentTitle(snapshot)} cannot be generated without this.`;
}

function valueRow(snapshot: InsuranceSnapshot): RowFacts {
    const valued = snapshot.policy?.valuedAt ?? null;
    if (valued === null || valued === undefined) {
        return yourMove('Not set', `What the vehicle is insured for. ${blockingNote(snapshot)}`);
    }
    return done(money(valued));
}

/**
 * The cover period — the two days the whole year is judged against.
 *
 * COVER THAT HAS ENDED IS STILL RECORDED, so the row stays done and the fact is a note rather than
 * a demand: the period is a statement about a document already filed, and nobody can retype it into
 * the future. The narrator leads with the same fact, which is where an admin will read it first.
 */
function periodRow(snapshot: InsuranceSnapshot, now: Date): RowFacts {
    const period = policyPeriodText(snapshot.policy?.startsAt, snapshot.policy?.expiresAt);
    if (!period) {
        return yourMove(
            'Not set',
            `Both instruments print the insurance period. ${blockingNote(snapshot)}`,
        );
    }
    const ended = coverEndedOn(snapshot, now);
    return {
        state: 'done',
        detail: period,
        note: ended ? `Cover ended ${ended}.` : null,
        meta: { doneDate: dateText(snapshot.policy?.startsAt) },
    };
}

/**
 * The certificate — a RECORD, because cover is a fact about the insurer's policy and not about the
 * paper this app happens to hold.
 *
 * AN ATTESTED FILING SAYS SO [D-B56]. A scan nobody could read may be filed on an admin's own
 * reading of it, and a row that printed `On file` over it without saying which would hide the one
 * fact a later reader needs.
 */
function certificateRow(snapshot: InsuranceSnapshot): RowFacts {
    const policy = snapshot.policy;
    if (!policy || !isFilled(policy.certificateUrl)) {
        return record(false, 'Not on file', 'The Certificate of Insurance is the evidence of cover.');
    }
    const attested = dateText(policy.certificateAttestedAt);
    return record(
        true,
        'On file',
        attested ? `Filed on an admin’s own reading of the document on ${attested}.` : undefined,
    );
}

function premiumRow(snapshot: InsuranceSnapshot): RowFacts {
    const premium = snapshot.premium;
    if (!premium || premium.transactionId === null) {
        return yourMove('Not recorded', `${PREMIUM_QUEUE} ${blockingNote(snapshot)}`);
    }
    const parts = [money(premium.amount), dateText(premium.paidOn)].filter(Boolean);
    return done(parts.join(' · ') || 'Linked', premium.paidOn);
}

function discountRow(snapshot: InsuranceSnapshot): RowFacts {
    const discount = snapshot.discount;
    if (!discount || discount.transactionId === null) return record(false, 'None recorded');
    const parts = [money(discount.amount), dateText(discount.paidOn)].filter(Boolean);
    return record(true, parts.join(' · ') || 'Linked');
}

/**
 * The premium the year is written over — `insurance_loans.amount`, and never the amount advanced.
 *
 * IT IS THE FIRST ROW OF BOTH INSTRUMENTS' MONEY GRIDS, which is why it is `render` on either: the
 * Advance prints it as `Premium for the insurance period` and the Record as `Amount paid for the
 * insurance period`, and neither can be generated without it.
 */
function amountRow(snapshot: InsuranceSnapshot): RowFacts {
    const amount = snapshot.loan?.amount ?? null;
    if (amount === null || amount === undefined) {
        return yourMove('Not set', `The premium for the insurance period. ${blockingNote(snapshot)}`);
    }
    return done(money(amount));
}

function rateRow(snapshot: InsuranceSnapshot): RowFacts {
    if (isPaymentRecord(snapshot)) return record(false, 'None', PAID_IN_FULL_NO_RATE);
    const rate = snapshot.loan?.interestRate ?? null;
    if (rate === null || rate === undefined) return record(false, 'Not set');
    return record(true, `${rate}%`);
}

/**
 * What the driver has paid towards the premium — the Σ of the loan's deposit links [D-B45].
 *
 * EVERY ROW, NOT THE ONE THE DOSSIER BINDS. One production loan carries two (3000 + 977 against a
 * premium of 3977), and the second is what makes that year paid in full; a row that reported only
 * the editable link would contradict the instrument printed above it.
 */
function depositRow(snapshot: InsuranceSnapshot): RowFacts {
    const total = depositTotal(snapshot.deposits);
    if (nothingPaid(snapshot)) return record(false, 'Nothing paid');
    const paidOn = dateText(latestPaidOn(snapshot.deposits));
    return record(true, [money(total), paidOn].filter(Boolean).join(' · '));
}

/**
 * The weekly pair's face on a year settled at the end of the contract [D-B57] — `legacyPair`'s
 * shape, one dossier over.
 *
 * `record(false, …)` AND NOT `done`, because nothing is on file: the pair states what is true of
 * the year instead of naming a figure somebody has yet to provide, which is exactly what the
 * record class means.
 *
 * A FROZEN YEAR DROPS THE INVITATION. `freeze` leaves a settled record alone — rightly, since the
 * arrangement is correct as signed and there is nothing to unlock — so the note is the only place
 * this row can tell the truth about a canvas whose boxes have become reads.
 *
 * THE VALUE IS THE CALLER'S BECAUSE THE TWO ROWS ARE ASKED DIFFERENT QUESTIONS [D-B57]. "Weekly
 * recovery" is asked what rides on the instalment, and the arrangement's own name answers it;
 * "Recovery starts" is asked WHEN, where a reader expects a moment and a repeated arrangement name
 * answers nothing. Everything else is shared: one class, one dormant mark, one note.
 */
function atEndPair(snapshot: InsuranceSnapshot, detail: string): RowFacts {
    const note = snapshot.frozen ? SETTLED_AT_END : SETTLED_AT_END_OPEN;
    return record(false, detail, note);
}

function weeklyRow(snapshot: InsuranceSnapshot): RowFacts {
    if (isPaymentRecord(snapshot)) return record(false, 'Not recovered', PAID_IN_FULL_NO_RECOVERY);
    if (isAtEndAdvance(snapshot)) return atEndPair(snapshot, 'Settled at the end of the contract');
    const amount = latestInstalment(snapshot.instalments)?.amount ?? null;
    if (amount === null || amount === undefined) {
        return yourMove(
            'Not set',
            `The advance is recovered weekly, on top of the contract instalment. ${blockingNote(snapshot)}`,
        );
    }
    return done(money(amount));
}

/**
 * THE DAY RECOVERY BEGINS, AND ON AN AT-END YEAR THE ONLY HONEST ANSWER IS A MILESTONE [D-B57].
 * There is no date to state — the advance waits on the vehicle being paid for, which is a point no
 * calendar on this page knows — so the row names that point. The note beside it says what happens
 * from there: the driver keeps paying the contract instalment until the advance is recovered.
 */
function firstRow(snapshot: InsuranceSnapshot): RowFacts {
    if (isPaymentRecord(snapshot)) return record(false, 'Not recovered', PAID_IN_FULL_NO_RECOVERY);
    if (isAtEndAdvance(snapshot)) return atEndPair(snapshot, 'After the vehicle is paid for');
    const starts = firstStartsAt(snapshot.instalments);
    if (!starts) {
        return yourMove(
            'Not set',
            `The Schedule states the day recovery begins. ${blockingNote(snapshot)}`,
        );
    }
    return done(dateText(starts));
}

/**
 * The Parties row — the instruments' own SEVEN, in one line.
 *
 * SEVEN AND NOT THE ADVANCE'S FIVE: both insurance templates print the year of manufacture and the
 * colour in their particulars grid, and both are render-blocking merge keys like the rest. It
 * COUNTS what is missing rather than naming the first gap, because an admin who fixes one wants to
 * know how many are left; the fields themselves are edited on the contract's own driver and vehicle
 * areas, which the block beneath this row links to.
 */
function partiesRow(snapshot: InsuranceSnapshot): RowFacts {
    const { driver, vehicle } = snapshot;
    const year = vehicle.year === null || vehicle.year === undefined ? null : String(vehicle.year);
    const fields: Array<[string, string | null]> = [
        ['the borrower’s name', driver.fullName],
        ['their address', driver.address],
        ['their phone', driver.phone],
        ['the vehicle', vehicle.model],
        ['the year of manufacture', year],
        ['the colour', vehicle.colour],
        ['the registration', vehicle.registration],
    ];
    const gaps = fields.filter(([, value]) => !isFilled(value)).map(([label]) => label);
    if (gaps.length === 0) {
        const who = isFilled(driver.fullName) ? (driver.fullName as string) : 'On file';
        return done(who);
    }
    const missing = gaps.length === 1 ? gaps[0] : `${gaps.length} details`;
    return yourMove(
        `Missing ${missing}`,
        `The ${instrumentTitle(snapshot)} prints the parties and the Vehicle. ${blockingNote(snapshot)}`,
    );
}

/**
 * The hire purchase reference — the number both instruments quote.
 *
 * IT IS THE SERVER'S ANSWER, NOT A LOOKUP OF OUR OWN. `agreement_merge_data` resolves it from the
 * live hire purchase DOCUMENT, and [D-B52] adds the paper designation `P-<contract id>` for a
 * contract whose hire purchase agreement was signed on paper. A null here is a document that would
 * be handed to a driver with `{{contract.hire_purchase_reference}}` printed literally.
 */
function hpReferenceRow(snapshot: InsuranceSnapshot): RowFacts {
    if (isFilled(snapshot.hpReference)) return done(snapshot.hpReference as string);
    return yourMove(
        'Not resolved',
        'Both instruments quote the hire purchase agreement’s number, and this contract has none ' +
            `yet. ${blockingNote(snapshot)}`,
    );
}

/** The instrument already carries a signature of its own — a paper original, or a signed seat. */
function instrumentStarted(snapshot: InsuranceSnapshot): boolean {
    const slot = snapshot.slot;
    if (!slot) return false;
    if (slot.legacySignedAt) return true;
    return (slot.document?.signatures ?? []).some(
        (signature) => signature.party !== 'COMPANY_REP' && SIGNED_STATUSES.has(signature.status),
    );
}

/**
 * THE D-B55 LOCK — the driver's hire purchase signature, which is what opens this whole ceremony.
 *
 * IT LIFTS THE MOMENT THE INSTRUMENT HAS A SIGNATURE OF ITS OWN: re-locking a document a party has
 * already signed would erase their signature from the rail, and voiding the hire purchase agreement
 * underneath an instrument in flight is precisely that shape.
 */
function hpSignatureRow(snapshot: InsuranceSnapshot): RowFacts {
    if (snapshot.hpDriverSigned) return done('Signed');
    if (instrumentStarted(snapshot)) {
        return {
            state: 'done',
            detail: 'Not blocking',
            note: 'This instrument was signed before the hire purchase signature went away, so the gate no longer holds it.',
            meta: {},
        };
    }
    return locked('Locked', HP_SIGNATURE_FIRST);
}

/**
 * Why Primelane cannot countersign yet, or null.
 *
 * THE BOARD'S OWN PREDICATE, SPELLED THE SAME WAY. `agreement_sign` refuses the countersignature
 * until the driver has signed the hire purchase agreement AND the witness has signed the witness
 * agreement [D-B27, D-B55]; the driver's arm is named first when both are outstanding, because the
 * witness cannot sign until the driver has.
 *
 * AND THE WITNESS ARM HAS TWO CARVE-OUTS, BOTH THE SERVER'S. A Payment Record drops it [D-B45] —
 * `agreement_sign` skips the witness limb on an `insurance_payment` document, because D-B27's
 * rationale is the credit exposure a third bargain creates and nothing is advanced under the
 * Record. A paper hire purchase master drops it too [D-B53]: a witness agreement attests to an
 * IN-APP driver signature and will never be generated for one executed offline. Without either
 * waiver this rail says "the witness has to sign" over a board correctly offering the countersign
 * button — the mirror problem the manifest already fixed twice.
 *
 * THE SLOT'S INSTRUMENT AND NOT THE SNAPSHOT'S. The gate is about the document out for signature,
 * and the draft on screen may already be a different shape.
 */
function countersignBlockedNote(snapshot: InsuranceSnapshot): string | null {
    if (!snapshot.hpDriverSigned) return DRIVER_BEFORE_COUNTERSIGN;
    const slot = snapshot.slot;
    if ((slot?.instrument ?? 'ADVANCE') === 'PAYMENT_RECORD') return null;
    if (!snapshot.witnessSigned && !slot?.masterOnPaper) return WITNESS_BEFORE_COUNTERSIGN;
    return null;
}

/**
 * The instrument's own state — not created, out for signature, countersigned, sealed, or paper.
 *
 * THE BORROWER SIGNS IN THE DRIVER APP AND NOT BY LINK, which is why no token is read here:
 * `agreement_issue_token` has no BORROWER path at all, so there is no "sent" to date and no open
 * count to report. The wait is named by the person, and the place they sign is named with it.
 */
function documentRow(snapshot: InsuranceSnapshot, now: Date): RowFacts {
    const slot = snapshot.slot;

    if (isPaperInsurance(slot)) {
        const signed = dateText(slot!.legacySignedAt);
        return record(true, signed ? `Signed on paper ${signed}` : 'Signed on paper');
    }

    // THE SEAL IS ASKED FIRST. A finalized ceremony is a finished one, and a row that went on
    // reading `2 of 2 signed` off the signature list would hold the whole area open behind a
    // document nothing is waiting for.
    if (isInsuranceSealed(slot)) {
        const executed = dateText(slot!.legacySignedAt);
        return done(executed ? `Executed ${executed}` : 'Executed', slot!.legacySignedAt);
    }

    if (!slot || !slot.document) {
        if (noLoanYet(snapshot)) return record(false, 'Not created', NOTHING_TO_FINANCE);
        if (!snapshot.hpDriverSigned && !instrumentStarted(snapshot)) {
            return locked('Locked', HP_SIGNATURE_FIRST);
        }
        return yourMove('Not generated', `Generate the ${instrumentTitle(snapshot)} to start the signing.`);
    }

    const document = slot.document;

    if (document.status === 'VOIDED' || document.status === 'SUPERSEDED') {
        return yourMove('Voided', `The ${instrumentTitle(snapshot)} was voided. Generate it again.`);
    }

    const signed = signedCount(document);
    const total = document.signatures.length;

    if (document.status === 'COMPLETED' && !document.finalizedAt) {
        const startedAt = newestSignedAt(document);
        const elapsed = startedAt ? Math.max(0, differenceInSeconds(now, asDate(startedAt) as Date)) : 0;
        return {
            state: 'waiting',
            detail: `sealing · ${elapsed}s`,
            note: 'Signed by every party · sealing the executed PDF — this finishes on its own.',
            meta: { sealElapsedSeconds: elapsed, actor: 'Primelane' },
        };
    }

    const pending = pendingSignature(document);
    if (!pending) {
        return { state: 'waiting', detail: `${signed} of ${total} signed`, note: null, meta: {} };
    }

    if (pending.party === 'COMPANY_REP') {
        const blocked = countersignBlockedNote(snapshot);
        if (blocked) {
            return { state: 'waiting', detail: `${signed} of ${total} signed`, note: blocked, meta: {} };
        }
        return yourMove(
            `${signed} of ${total} signed`,
            'The driver has signed. Countersign as Primelane.',
        );
    }

    // THE UNRESOLVED MERGE FIELDS ARE WORTH SAYING ONLY WHILE THEY CAN STILL BE FIXED. Nothing is
    // frozen until the first signature, so a document nobody has signed can be voided and reissued
    // against corrected figures; after that the sentence would be a complaint with no door.
    const unresolved = signed === 0 ? (snapshot.renderMissing ?? []).length : 0;
    const preflight = unresolved
        ? ` ${unresolved} ${unresolved === 1 ? 'field the document quotes is' : 'fields the document quotes are'} still unresolved.`
        : '';

    return {
        state: 'waiting',
        detail: `${signed} of ${total} signed`,
        note: `Waiting on ${driverName(snapshot)} to sign in the driver app.${preflight}`,
        meta: { actor: driverName(snapshot) },
    };
}

/**
 * EXECUTED — the terminal row, and the house's own seal predicate.
 *
 * THE TWO HALVES ARE BOTH THE GATE: a `signed_at` with no `agreement_url` is not executed as far as
 * anything that consumes the pair is concerned, so the row says what is true of it rather than
 * ticking on half the evidence.
 */
function sealedRow(snapshot: InsuranceSnapshot, now: Date): RowFacts {
    const slot = snapshot.slot;

    if (isInsuranceSealed(slot)) {
        const executed = dateText(slot!.legacySignedAt);
        const paper = isPaperInsurance(slot);
        return {
            state: 'done',
            detail: executed ? `Executed ${executed}` : 'Executed',
            note: paper ? 'Signed on paper.' : null,
            meta: { doneDate: executed },
        };
    }

    if (slot?.legacySignedAt) {
        return { state: 'waiting', detail: 'Filing', note: FILING, meta: { actor: 'Primelane' } };
    }

    const document = slot?.document ?? null;
    if (document && document.status === 'COMPLETED' && !document.finalizedAt) {
        const startedAt = newestSignedAt(document);
        const elapsed = startedAt ? Math.max(0, differenceInSeconds(now, asDate(startedAt) as Date)) : 0;
        return {
            state: 'waiting',
            detail: `sealing · ${elapsed}s`,
            note: 'Sealing the executed PDF — this finishes on its own.',
            meta: { sealElapsedSeconds: elapsed, actor: 'Primelane' },
        };
    }

    if (noLoanYet(snapshot)) return record(false, 'Not executed', NOTHING_TO_FINANCE);

    return locked('Not executed', 'Both parties have to sign the instrument.');
}

function rowFacts(def: InsuranceRowDef, snapshot: InsuranceSnapshot, now: Date): RowFacts {
    switch (def.id) {
        case 'pol-value':
            return valueRow(snapshot);
        case 'pol-period':
            return periodRow(snapshot, now);
        case 'pol-certificate':
            return certificateRow(snapshot);
        case 'pol-premium':
            return premiumRow(snapshot);
        case 'pol-discount':
            return discountRow(snapshot);
        case 'pol-amount':
            return amountRow(snapshot);
        case 'pol-rate':
            return rateRow(snapshot);
        case 'pol-deposit':
            return depositRow(snapshot);
        case 'pol-weekly':
            return weeklyRow(snapshot);
        case 'pol-first':
            return firstRow(snapshot);
        case 'pol-parties':
            return partiesRow(snapshot);
        case 'pol-hp-reference':
            return hpReferenceRow(snapshot);
        case 'pol-hp-signature':
            return hpSignatureRow(snapshot);
        case 'pol-document':
            return documentRow(snapshot, now);
        case 'pol-sealed':
            return sealedRow(snapshot, now);
    }
}

/**
 * THE CLASS IS THE ROW'S DEFAULT ANSWER AND THIS READS THE YEAR ON TOP OF IT — `rowClassOf`'s rule
 * from the contract rail, applied to the three shapes that have one.
 *
 * THE WEEKLY PAIR FALLS TO `record` ON A PAYMENT RECORD [D-B45]. Nothing is owed there and nothing
 * anybody types can change it — the save itself drops a typed pair the moment the deposit covers
 * the premium — so the pair states a fact about the year instead of a figure somebody has yet to
 * provide, which is exactly what the record class means.
 *
 * AND ON A YEAR SETTLED AT THE END OF THE CONTRACT [D-B57], for the first half of that reason:
 * nothing is owed weekly there either. The second half does not hold — a weekly recovery is a
 * legitimate write on such a year — which is what the fields staying open is about, not the class.
 *
 * THE DOCUMENT ROW FALLS TO `record` AT BOTH ENDS OF ITS LIFE: before a loan exists there is no
 * subject to generate an instrument over, and on a paper original there never will be one.
 */
function rowClassOf(def: InsuranceRowDef, snapshot: InsuranceSnapshot): RowClass {
    if (
        (isPaymentRecord(snapshot) || isAtEndAdvance(snapshot)) &&
        (def.id === 'pol-weekly' || def.id === 'pol-first')
    ) {
        return 'record';
    }
    if (def.id === 'pol-document' && (noLoanYet(snapshot) || isPaperInsurance(snapshot.slot))) {
        return 'record';
    }
    if (def.id === 'pol-sealed' && noLoanYet(snapshot)) return 'record';
    return def.cls;
}

/**
 * DORMANT: nothing is owed on this row and nothing the admin types can change that, so it is out of
 * both halves of its group's counter and wears the grey "nothing to do here yet" mark.
 *
 * FOUR SHAPES DECLARE IT. The first is the Payment Record's three [D-B45]: the rate, the weekly
 * recovery and the day it starts are all figures about an advance, and this year is not one —
 * `0/3` over three lines nobody owes is the counter describing a list nobody is looking at, and the
 * group reads `Paid in full` instead.
 *
 * THE SECOND IS A JUNCTION WITH NO LOAN, and it takes BOTH agreement rows — the document and the
 * seal on it. 54 live junctions are that shape: cover recorded, nothing financed, no subject for an
 * instrument at all. `advanceState` makes the same exclusion for the document of an advance that
 * does not exist yet; the Executed gate joins it here because an insurance year, unlike an advance
 * created from this very flow, may legitimately end without any instrument ever being owed.
 *
 * THE THIRD IS A YEAR SETTLED AT THE END OF THE CONTRACT [D-B57] — the weekly pair, and the rate
 * is NOT one of them, because interest is charged on an at-end advance exactly as it is on any
 * other. It is here for the counter's sake: `record(false, …)` is settled and not done work, so
 * rows left in would read `3/5` under two blue dots over a pair nobody owes.
 *
 * THE FOURTH IS A PREMIUM THE DRIVER HAS PAID NOTHING TOWARDS, on every advance shape this rail
 * draws — weekly and settled at the end alike, since nothing separates the two here and nothing on
 * the server ever refuses over a missing part payment. "Paid by the driver" RECORDS one and never
 * asks for one: the canvas block above it is headed `records, not requirements`, and 283 of the 308
 * production insurance loans carry no deposit row at all — so the blue dot was saying `outstanding`
 * about the ordinary shape of the thing, over an area reading `2/3`. It is [D-B41] one dossier
 * over, where the contract with no `contract_deposits` row makes BOTH its deposit rows dormant on
 * exactly this reasoning, and the owner ruled it here on 2026-09-22. The moment anything IS paid
 * the row is a recorded record again — counted, ticked, and reading the money and the day.
 *
 * AND THE LAST TWO KEEP THEIR FIELDS OPEN, WHICH THE FIRST TWO DO NOT. Nothing anybody types can
 * move a Payment Record's rate or a document whose subject does not exist; a weekly recovery, by
 * contrast, is a legitimate optional write on an at-end year — 37 of them carry one — so the canvas
 * goes on offering the boxes while the counter stops asking for them. A part payment is the same
 * kind of write and keeps the same invitation: the block stays on the canvas, the row keeps its
 * jump into it, and only the asking stops.
 *
 * THE REST OF THE AGREEMENT AREA IS NOT DORMANT ON THOSE, deliberately: the parties, the hire
 * purchase reference and its signature are facts about the contract that stay true — and stay
 * work — whether or not this year is financed.
 */
function rowDormantOf(def: InsuranceRowDef, snapshot: InsuranceSnapshot): boolean {
    if (isPaymentRecord(snapshot) && (def.id === 'pol-rate' || def.id === 'pol-weekly' || def.id === 'pol-first')) {
        return true;
    }
    if (isAtEndAdvance(snapshot) && (def.id === 'pol-weekly' || def.id === 'pol-first')) return true;
    if (def.id === 'pol-deposit' && nothingPaid(snapshot)) return true;
    return (def.id === 'pol-document' || def.id === 'pol-sealed') && noLoanYet(snapshot);
}

/**
 * THE ROWS A SIGNED INSTRUMENT PRINTS, and therefore the rows that freeze with it.
 *
 * IT IS A LIST OF ROWS AND NOT OF AREAS, because the freeze is a claim about a DOCUMENT rather than
 * about a canvas. Every entry here is a merge key one of the two instruments quotes: the cover
 * period is `insurance.period_start`/`period_end` on both particulars grids, and the five advance
 * figures are `insurance.premium_amount`, `insurance_loan.interest_rate`, `.deposit_amount`,
 * `.weekly_instalment` and `.first_instalment_date`.
 *
 * WHAT IS DELIBERATELY ABSENT IS AS MUCH OF THE RULE AS WHAT IS HERE. The insured value and the
 * certificate are printed by neither instrument, so neither can contradict a signed page — an admin
 * correcting a typo'd insured value on an executed year is fixing a record, not rewriting a
 * document. The PREMIUM area is absent for the same reason and it is the less obvious one: the
 * payment made to the insurer is an `insurance_premiums` link nothing prints, and the Record's
 * "Paid by the Driver" reads the DEPOSITS. And the agreement area is absent because freezing the
 * rows that describe the ceremony would lock the ceremony itself.
 */
const FROZEN_ROWS: InsuranceRowId[] = [
    'pol-period',
    'pol-amount',
    'pol-rate',
    'pol-deposit',
    'pol-weekly',
    'pol-first',
];

/**
 * THE FROZEN DEMOTION — a figure the signed instrument printed is not the admin's move any more.
 *
 * Once `frozen_at` is set those rows render read-only on the canvas, so a blue dot over a row
 * nobody can type into would be the rail pointing at a door the canvas has shut. The correction is
 * void-and-reissue on the agreement row, and the unlock text says so rather than leaving an admin to
 * guess. Rows already settled keep their receipts: freezing does not un-record what is on file.
 */
function freeze(row: RowFacts, def: InsuranceRowDef, snapshot: InsuranceSnapshot): RowFacts {
    if (!snapshot.frozen) return row;
    if (!FROZEN_ROWS.includes(def.id)) return row;
    if (row.state === 'done') return row;
    return locked(row.detail, FROZEN_UNLOCK);
}

// ─── The whole rail ──────────────────────────────────────────────────────────

function receiptFor(area: InsuranceArea, snapshot: InsuranceSnapshot): string {
    if (area === 'advance' && isPaymentRecord(snapshot)) return 'Paid in full';
    if (area === 'advance' && isAtEndAdvance(snapshot)) return 'Settled at the end of the contract';
    if (area === 'agreement') {
        const executed = dateText(snapshot.slot?.legacySignedAt);
        return executed ? `Executed ${executed}` : AREA_RECEIPTS.agreement;
    }
    return AREA_RECEIPTS[area];
}

/**
 * The one sentence under the policy's name, and what to do about it.
 *
 * THE PRECEDENCE IS THE CEREMONY'S OWN ORDER, READ BACKWARDS: what is finished outranks what is in
 * flight, which outranks what has not started — so the band always names the furthest point this
 * year has actually reached rather than the first unfilled thing on the rail.
 *
 * TWO PREFIXES LEAD THE LIVE STATES, AND NEITHER REPLACES THE SENTENCE. "Cover ended <date>." is
 * the fact an admin most needs in front of them on a year whose period has passed — the work below
 * it is still the work. "Figures are locked." is the frozen instrument's, and only while it is
 * still in flight: an executed instrument's figures have been locked since the first signature, and
 * saying so over "Executed Sep 12, 2026." would spend the one line on the page that changes telling
 * an admin something the date beside it already implies.
 */
function narratorFor(snapshot: InsuranceSnapshot, now: Date): InsuranceNarrator {
    const slot = snapshot.slot;
    const openAgreement: InsuranceNarratorAction = {
        id: 'open-insurance-agreement',
        label: 'Open the agreement',
        kind: 'primary',
        intent: 'jump',
        rowId: 'pol-document',
        area: 'agreement',
    };

    if (isPaperInsurance(slot)) {
        const signed = dateText(slot!.legacySignedAt);
        return {
            sentence: signed ? `Signed on paper on ${signed}.` : 'Signed on paper.',
            actions: [],
        };
    }

    if (isInsuranceSealed(slot)) {
        const executed = dateText(slot!.legacySignedAt);
        return { sentence: executed ? `Executed ${executed}.` : 'Executed.', actions: [] };
    }

    const ended = coverEndedOn(snapshot, now);
    const prefix = (sentence: string) => {
        const lead = [ended ? `Cover ended ${ended}.` : '', snapshot.frozen ? 'Figures are locked.' : '']
            .filter(Boolean)
            .join(' ');
        return lead ? `${lead} ${sentence}` : sentence;
    };

    const document = slot?.document ?? null;
    if (document && document.status !== 'VOIDED' && document.status !== 'SUPERSEDED') {
        if (document.status === 'COMPLETED' && !document.finalizedAt) {
            return { sentence: prefix('Sealing the executed PDF.'), actions: [] };
        }
        if (slot?.legacySignedAt) {
            return { sentence: prefix(FILING), actions: [] };
        }
        const pending = pendingSignature(document);
        if (pending?.party === 'COMPANY_REP') {
            const blocked = countersignBlockedNote(snapshot);
            return {
                sentence: prefix(blocked ?? 'Ready for Primelane’s countersignature.'),
                actions: blocked ? [] : [openAgreement],
            };
        }
        if (pending) {
            return {
                sentence: prefix(`Waiting on ${driverName(snapshot)} to sign in the driver app.`),
                actions: [],
            };
        }
        // Every seat signed and the document not yet COMPLETED — the seal is what follows.
        return { sentence: prefix('Sealing the executed PDF.'), actions: [] };
    }

    if (noPolicyYet(snapshot)) {
        return {
            sentence: 'Set out the cover, what the premium cost and how it comes back, then create the policy.',
            actions: [],
        };
    }

    if (!snapshot.hpDriverSigned && !instrumentStarted(snapshot)) {
        return { sentence: prefix(HP_SIGNATURE_FIRST), actions: [] };
    }

    // NOTHING FINANCED IS A FINISHED YEAR, NOT AN UNSTARTED ONE. There is no instrument to
    // generate over a junction with no loan, so the band states the cover rather than pointing at
    // a door `agreement_create` would refuse.
    if (noLoanYet(snapshot)) {
        return { sentence: prefix('Recorded. Nothing was financed on this year.'), actions: [] };
    }

    return {
        sentence: prefix(`Recorded. Generate the ${instrumentTitle(snapshot)} when the figures are right.`),
        actions: [openAgreement],
    };
}

/**
 * One snapshot in, the whole rail out.
 *
 * Nothing here reads a clock, a query cache or the DOM.
 */
export function computeInsuranceState(snapshot: InsuranceSnapshot): InsuranceState {
    const now = asDate(snapshot.now) ?? new Date();
    const drafts = snapshot.drafts ?? {};

    const rows: InsuranceRow[] = ROW_DEFS.map((def) => {
        const facts = freeze(rowFacts(def, snapshot, now), def, snapshot);
        const draft = drafts[def.id];
        const meta: RowMeta = { ...(facts.meta ?? {}), ...(draft ? { draft } : {}) };
        return {
            id: def.id,
            area: def.area,
            label: def.label,
            cls: rowClassOf(def, snapshot),
            optional: rowDormantOf(def, snapshot),
            state: facts.state,
            detail: facts.detail,
            note: facts.note ?? null,
            meta,
            jump: { area: def.area, block: def.block, ...(def.focus ? { focus: def.focus } : {}) },
            // NO SERVER REFUSAL LANDS ON THIS RAIL. `sign_contract` grades a contract; a policy year
            // is written with ordinary statements whose failures are reported where they happen.
            refusal: null,
        };
    });

    const areaGroups: InsuranceAreaGroup[] = INSURANCE_AREAS.map((area, index) => {
        const groupRows = rows.filter((row) => row.area === area.key);
        const counted = groupRows.filter(isCountedRow);
        const satisfied = groupRows.every((row) => row.state === 'done');
        return {
            area: area.key,
            label: area.label,
            index: index + 1,
            rows: groupRows,
            doneCount: counted.filter(isRowSettled).length,
            requiredCount: counted.length,
            satisfied,
            receipt: satisfied ? receiptFor(area.key, snapshot) : null,
            // NO SERVER n-OF-m. `get_contract_setup_status` grades a contract's setup and knows
            // nothing about one year of cover, so the rail's own count is the only count there is.
            serverCounts: null,
        };
    });

    const progress: SetupProgress = {
        areasComplete: areaGroups.filter((group) => group.satisfied).length,
        areaCount: areaGroups.length,
        needYou: rows.filter((row) => row.state === 'your-move').length,
        waitingOnOthers: rows.filter((row) => row.state === 'waiting').length,
        outstanding: rows.filter((row) => row.state !== 'done').length,
    };

    return { rows, areaGroups, progress, narrator: narratorFor(snapshot, now) };
}

/**
 * The area an admin who arrived without one should be put in front of.
 *
 * THE YEAR'S OWN ORDER, AND NOTHING CLEVERER. Cover is bought, paid for, financed and papered, and
 * the first area carrying something to do is where the page opens; with nothing to do at all it
 * opens on whatever is still in flight, and failing that on the agreement, which is where a
 * finished year's record lives.
 */
export function resolveInsuranceLandingArea(state: InsuranceState): InsuranceArea {
    const startable = state.rows.find((row) => row.state === 'your-move');
    if (startable) return startable.area;
    const inFlight = state.rows.find((row) => row.state === 'waiting' || row.state === 'locked');
    if (inFlight) return inFlight.area;
    return 'agreement';
}

/**
 * What the page is called.
 *
 * THE ID AND NOT THE INSURER'S NAME, because a policy row carries no name to print: `insurances`
 * holds a value, a period and a certificate. The id is what the URL names, what the delete RPC
 * takes and what an admin can quote back — and `New policy` is the one heading that is true before
 * any of that exists.
 */
export function insuranceHeading(subject: number | 'new'): string {
    return subject === 'new' ? 'New policy' : `Policy #${subject}`;
}
